import axios from 'axios';

const AUTH_URL = 'https://auth-linc.sandscode.com/realms/lincoln-production/protocol/openid-connect/token';
const CLIENT_ID = 'lincoln-frontend-apps';

const TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const TOKEN_EXPIRY_KEY = 'token_expiry';

export const authService = {
  async login(username, password) {
    try {
      const params = new URLSearchParams();
      params.append('grant_type', 'password');
      params.append('client_id', CLIENT_ID);
      params.append('username', username);
      params.append('password', password);

      const response = await axios.post(AUTH_URL, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token, refresh_token, expires_in } = response.data;

      // Calculate token expiry time
      const expiryTime = Date.now() + expires_in * 1000;

      // Store tokens in localStorage
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.error_description || 'Login failed. Please check your credentials.',
      };
    }
  },

  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const params = new URLSearchParams();
      params.append('grant_type', 'refresh_token');
      params.append('client_id', CLIENT_ID);
      params.append('refresh_token', refreshToken);

      const response = await axios.post(AUTH_URL, params, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      const { access_token, refresh_token, expires_in } = response.data;

      // Calculate token expiry time
      const expiryTime = Date.now() + expires_in * 1000;

      // Update tokens in localStorage
      localStorage.setItem(TOKEN_KEY, access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
      localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());

      return { success: true, data: response.data };
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      return { success: false, error: 'Session expired. Please login again.' };
    }
  },

  logout() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  },

  getAccessToken() {
    return localStorage.getItem(TOKEN_KEY);
  },

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },

  isAuthenticated() {
    const token = this.getAccessToken();
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !expiry) {
      return false;
    }

    // Check if token is expired (with 30 second buffer)
    const isExpired = Date.now() > parseInt(expiry) - 30000;

    if (isExpired) {
      return false;
    }

    return true;
  },

  async ensureValidToken() {
    if (this.isAuthenticated()) {
      return this.getAccessToken();
    }

    // Try to refresh the token
    const refreshResult = await this.refreshToken();
    if (refreshResult.success) {
      return this.getAccessToken();
    }

    return null;
  },

  getUserAttributes() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      console.log('No access token found');
      return null;
    }
    const decoded = decodeJwt(token);
    return decoded;
  }

};

function decodeJwt(token) {
  const payload = token.split('.')[1];
  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  return JSON.parse(atob(base64));
}
